import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OperatorPageShell, Button } from '@/components';
import { useAuthFetch } from '@/hooks';
import { formatDateTimeFull } from '@/utils';
import { ROUTES } from '../../routes.ts';
import { PAGE_SIZE, TABLE_BTN } from './operatorTableConstants.ts';
import { Th, OperatorPagination, Pill } from './operatorTableHelpers.tsx';
import panel from './operatorPanel.module.css';
import type { OperatorSupportTicketListResponse, OperatorSupportTicketSummary, SupportTicketStatus } from '@dermalink/shared';

function categoryLabel(category: string): string {
  return category === 'feedback' ? 'Feedback' : 'Ondersteuning';
}

export function OperatorSupportTicketsPanel() {
  const authFetch = useAuthFetch();
  const [data, setData]       = useState<OperatorSupportTicketListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [page, setPage]       = useState(1);
  const [status, setStatus]   = useState<SupportTicketStatus | ''>('open');

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    const params = new URLSearchParams({ page: String(page) });
    if (status) params.set('status', status);
    authFetch(`/operator/support-tickets?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OperatorSupportTicketListResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch, page, status]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div className={panel.section}>
      <div className={panel.toolbar}>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value as SupportTicketStatus | ''); setPage(1); }}
          className={panel.field}
        >
          <option value="open">Open</option>
          <option value="resolved">Opgelost</option>
          <option value="">Alles</option>
        </select>
      </div>

      {loading && <p className="text-muted status-message">Laden…</p>}
      {error   && <p className="text-muted status-message">Kon tickets niet laden.</p>}

      {data && !loading && (
        <>
          <div className={panel.resultCount}>{data.total} {data.total === 1 ? 'ticket' : 'tickets'}</div>
          <div className={panel.tableWrap}>
            <table className={panel.table}>
              <thead>
                <tr>
                  <Th>Datum</Th>
                  <Th>Arts</Th>
                  <Th>Type</Th>
                  <Th>Onderwerp</Th>
                  <Th>Status</Th>
                  <Th>Acties</Th>
                </tr>
              </thead>
              <tbody>
                {data.tickets.map((t) => <TicketRow key={t.id} ticket={t} onChanged={load} />)}
                {data.tickets.length === 0 && (
                  <tr><td colSpan={6} className={panel.emptyCell}>Geen tickets gevonden.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <OperatorPagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

export function OperatorSupportTickets() {
  return (
    <OperatorPageShell maxWidth="1400px">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to={ROUTES.operator} className={panel.mutedText}>← Beheerder</Link>
        <h1 className="page-heading" style={{ margin: 0 }}>Support tickets</h1>
      </div>
      <OperatorSupportTicketsPanel />
    </OperatorPageShell>
  );
}

function TicketRow({ ticket: t, onChanged }: { ticket: OperatorSupportTicketSummary; onChanged: () => void }) {
  const authFetch = useAuthFetch();
  const [expanded, setExpanded] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(false);

  async function handleResolve() {
    setResolving(true);
    setResolveError(false);
    try {
      const res = await authFetch(`/operator/support-tickets/${t.id}/resolve`, { method: 'POST' });
      if (!res.ok) throw new Error();
      onChanged();
    } catch {
      setResolveError(true);
    } finally {
      setResolving(false);
    }
  }

  return (
    <tr>
      <td className={panel.td} style={{ whiteSpace: 'nowrap', color: 'var(--c-text-muted)' }}>{formatDateTimeFull(t.created_at)}</td>
      <td className={panel.td}>{t.doctor_email}</td>
      <td className={panel.td}>{categoryLabel(t.category)}</td>
      <td className={panel.td} style={{ maxWidth: '360px' }}>
        <div>{t.subject}</div>
        <button type="button" onClick={() => setExpanded((v) => !v)} className={panel.linkButton}>
          {expanded ? 'Verbergen' : 'Toon bericht'}
        </button>
        {expanded && <div style={{ marginTop: '0.35rem', fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{t.body}</div>}
      </td>
      <td className={panel.td}>
        <Pill tone={t.status === 'open' ? 'warning' : 'success'}>{t.status === 'open' ? 'Open' : 'Opgelost'}</Pill>
      </td>
      <td className={panel.td}>
        {t.status === 'open' ? (
          <Button variant="ghost" style={TABLE_BTN} onClick={handleResolve} disabled={resolving}>
            {resolving ? '…' : 'Oplossen'}
          </Button>
        ) : (
          <span className={panel.mutedText}>{t.resolved_at ? formatDateTimeFull(t.resolved_at) : '—'}</span>
        )}
        {resolveError && <span className={panel.errorText} style={{ marginLeft: '0.5rem' }}>Mislukt</span>}
      </td>
    </tr>
  );
}
